import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';

import { useStores } from 'core';
import hasAccess, { Page } from 'helpers/hasAccess';
import { isTradingDeskUserOnly } from 'helpers/isTradingDeskUserOnly';
import { IAuthFormValues } from './helpers/validation';
import AuthMarkup from './Auth';

export const Auth = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const form = useRef<HTMLFormElement>(null);

  const { authStore } = useStores();

  useEffect(() => {
    authStore.checkAuth();
  }, [authStore.checkAuth]);

  useEffect(() => {
    const stateFrom = (location.state as { from?: { pathname: string } } | null)?.from;

    const prevPagePath = stateFrom?.pathname;
    const prevPageName =
      prevPagePath?.match(/\/advertiser\/\d+\/(?<page>\w+)/)?.groups?.page ??
      prevPagePath?.match(/^\/(?<page>[^/]+)/)?.groups?.page;
    const featureName = prevPageName?.replace(/-./g, (x) => x.toUpperCase()[1]);

    const cameFromAnotherPage = prevPagePath && prevPagePath !== '/app/auth';
    const hasAccessToPreviousPage = featureName && hasAccess(featureName as Page, 'pages');

    const defaultPath = isTradingDeskUserOnly(authStore.profile) ? '/trading-desk/adfox' : '/brands';

    const from =
      cameFromAnotherPage && hasAccessToPreviousPage
        ? stateFrom || ''
        : defaultPath;

    if (authStore.isAuthorized) {
      navigate(from, { replace: true });
    }
  }, [location, navigate, authStore.isAuthorized, authStore.profile]);

  const onSubmit = useCallback(
    (values: IAuthFormValues) => {
      const { username, password } = values;
      authStore.logIn(username, password);
    },
    [authStore.logIn],
  );

  return <AuthMarkup ref={form} onSubmit={onSubmit} isAuthorizing={authStore.isAuthorizing} />;
});
